import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/hooks/use-auth";
import { useMessageThread } from "@/hooks/use-message-thread";
import { useStores } from "@/hooks/use-stores";
import {
	type ThreadMessageSummary,
	type ThreadOrderSummary,
	type ThreadWithRelations,
	useThreads,
} from "@/hooks/use-threads";
import { cn, formatCurrency, formatDate, truncate } from "@/lib/utils";
import type { GeminiAnalysis } from "@/types";
import { ChevronDown, ExternalLink, Eye, MailWarning } from "lucide-react";
import { useMemo, useState } from "react";

const messageStatusVariant: Record<string, "success" | "warning" | "destructive" | "secondary"> = {
	processed: "success",
	processing: "warning",
	failed: "destructive",
	received: "secondary",
};

const intentVariant: Record<string, "success" | "warning" | "secondary"> = {
	order: "success",
	inquiry: "warning",
	other: "secondary",
};

const actionVariant: Record<string, "success" | "warning" | "destructive" | "secondary"> = {
	"Placed Shopify order": "success",
	"Created draft order": "warning",
	"Order creation failed": "destructive",
	"Processing failed": "destructive",
	"No order created": "secondary",
	"Awaiting processing": "secondary",
};

export function MessagesPage() {
	const [storeFilter, setStoreFilter] = useState<string | undefined>();
	const [statusFilter, setStatusFilter] = useState<string | undefined>();
	const [page, setPage] = useState(1);
	const [selectedThread, setSelectedThread] = useState<ThreadWithRelations | null>(null);
	const [retryingMessageId, setRetryingMessageId] = useState<string | null>(null);
	const [retryError, setRetryError] = useState<string | null>(null);

	const { session } = useAuth();
	const { stores } = useStores();
	const { threads, total, loading, error, refetch } = useThreads({
		storeId: storeFilter,
		status: statusFilter,
		page,
	});

	const totalPages = Math.ceil(total / 25);

	async function handleRetry(messageId: string) {
		if (!session?.access_token) {
			setRetryError("You must be signed in to retry failed messages.");
			return;
		}

		setRetryingMessageId(messageId);
		setRetryError(null);

		try {
			const res = await fetch("/api/retry-message", {
				method: "POST",
				headers: {
					Authorization: `Bearer ${session.access_token}`,
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ message_id: messageId }),
			});

			if (!res.ok) {
				throw new Error(await getErrorMessage(res, "Failed to retry message"));
			}

			await res.json();
			await refetch();
		} catch (err) {
			setRetryError(err instanceof Error ? err.message : "Failed to retry message");
		} finally {
			setRetryingMessageId(null);
		}
	}

	return (
		<div className="space-y-8">
			<div>
				<h1 className="text-3xl font-bold tracking-tight">Messages</h1>
				<p className="text-muted-foreground">
					Review customer threads and the actions Sunmerce took
				</p>
			</div>

			<div className="flex flex-wrap gap-3">
				<select
					className="rounded-md border border-input bg-background px-3 py-2 text-sm"
					value={storeFilter ?? ""}
					onChange={(e) => {
						setStoreFilter(e.target.value || undefined);
						setPage(1);
					}}
				>
					<option value="">All Stores</option>
					{stores.map((store) => (
						<option key={store.id} value={store.id}>
							{store.name}
						</option>
					))}
				</select>

				<select
					className="rounded-md border border-input bg-background px-3 py-2 text-sm"
					value={statusFilter ?? ""}
					onChange={(e) => {
						setStatusFilter(e.target.value || undefined);
						setPage(1);
					}}
				>
					<option value="">All Statuses</option>
					<option value="received">Received</option>
					<option value="processing">Processing</option>
					<option value="processed">Processed</option>
					<option value="failed">Failed</option>
				</select>
			</div>

			<Card>
				<CardContent className="p-0">
					{retryError && (
						<div className="border-b px-6 py-3 text-sm text-destructive">{retryError}</div>
					)}
					{loading ? (
						<div className="flex items-center justify-center py-12 text-muted-foreground">
							Loading threads...
						</div>
					) : error ? (
						<div className="flex items-center justify-center py-12 text-destructive">{error}</div>
					) : threads.length === 0 ? (
						<div className="flex flex-col items-center justify-center py-12 text-center">
							<MailWarning className="mb-3 h-8 w-8 text-muted-foreground" />
							<p className="text-muted-foreground">No threads found</p>
						</div>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Updated</TableHead>
									<TableHead>Store</TableHead>
									<TableHead>Customer</TableHead>
									<TableHead>Subject</TableHead>
									<TableHead>Latest Message</TableHead>
									<TableHead>Intent</TableHead>
									<TableHead>Action Taken</TableHead>
									<TableHead>Status</TableHead>
									<TableHead />
								</TableRow>
							</TableHeader>
							<TableBody>
								{threads.map((thread) => {
									const message = thread.latestInboundMessage;
									const order = getPrimaryOrder(message);
									const actionLabel = getActionLabel(message, order);
									const analysis = getAnalysis(message);
									const retryableMessage = getRetryableMessage(thread);

									return (
										<TableRow key={thread.id}>
											<TableCell className="whitespace-nowrap text-muted-foreground">
												{formatDate(thread.updated_at)}
											</TableCell>
											<TableCell>{thread.store?.name ?? "-"}</TableCell>
											<TableCell>
												<div>
													<p className="font-medium">
														{thread.participant_name ?? "Unknown sender"}
													</p>
													<p className="text-xs text-muted-foreground">
														{thread.participant_email}
													</p>
												</div>
											</TableCell>
											<TableCell className="max-w-xs">
												<p className="font-medium">
													{thread.subject ?? message?.subject ?? "No subject"}
												</p>
											</TableCell>
											<TableCell className="max-w-xs">
												<p className="text-xs text-muted-foreground">
													{truncate(
														message?.body_text ?? thread.messages?.[0]?.body_text ?? "",
														90,
													)}
												</p>
											</TableCell>
											<TableCell>
												<Badge variant={intentVariant[analysis?.intent ?? "other"] ?? "secondary"}>
													{analysis?.intent ?? "unknown"}
												</Badge>
											</TableCell>
											<TableCell>
												<Badge variant={actionVariant[actionLabel] ?? "secondary"}>
													{actionLabel}
												</Badge>
											</TableCell>
											<TableCell>
												<Badge
													variant={
														messageStatusVariant[message?.status ?? "received"] ?? "secondary"
													}
												>
													{message?.status ?? "received"}
												</Badge>
												{message?.last_error && (
													<p className="mt-1 max-w-xs text-xs text-destructive">
														{truncate(message.last_error, 80)}
													</p>
												)}
											</TableCell>
											<TableCell>
												<div className="flex items-center justify-end gap-2">
													{retryableMessage && (
														<Button
															variant="outline"
															size="sm"
															disabled={retryingMessageId === retryableMessage.id}
															onClick={() => void handleRetry(retryableMessage.id)}
														>
															{retryingMessageId === retryableMessage.id ? "Retrying..." : "Retry"}
														</Button>
													)}
													<Button
														variant="ghost"
														size="icon"
														className="h-8 w-8"
														onClick={() => setSelectedThread(thread)}
													>
														<Eye className="h-4 w-4" />
													</Button>
												</div>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>

			{totalPages > 1 && (
				<div className="flex items-center justify-center gap-2">
					<Button
						variant="outline"
						size="sm"
						disabled={page <= 1}
						onClick={() => setPage((currentPage) => currentPage - 1)}
					>
						Previous
					</Button>
					<span className="text-sm text-muted-foreground">
						Page {page} of {totalPages}
					</span>
					<Button
						variant="outline"
						size="sm"
						disabled={page >= totalPages}
						onClick={() => setPage((currentPage) => currentPage + 1)}
					>
						Next
					</Button>
				</div>
			)}

			{selectedThread && (
				<ThreadDetailModal
					thread={selectedThread}
					onRetry={handleRetry}
					retryingMessageId={retryingMessageId}
					onClose={() => setSelectedThread(null)}
				/>
			)}
		</div>
	);
}

function ThreadDetailModal({
	thread: threadSummary,
	onRetry,
	retryingMessageId,
	onClose,
}: {
	thread: ThreadWithRelations;
	onRetry: (messageId: string) => Promise<void>;
	retryingMessageId: string | null;
	onClose: () => void;
}) {
	const { thread, loading: threadLoading, error: threadError } = useMessageThread(threadSummary.id);
	const primaryMessage = useMemo(() => getPrimaryMessage(threadSummary), [threadSummary]);
	const order = useMemo(() => getPrimaryOrder(primaryMessage), [primaryMessage]);
	const actionLabel = useMemo(() => getActionLabel(primaryMessage, order), [primaryMessage, order]);
	const analysis = useMemo(() => getAnalysis(primaryMessage), [primaryMessage]);
	const retryableMessage = useMemo(() => getRetryableMessage(threadSummary), [threadSummary]);

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
			<Card className="max-h-[90vh] w-full max-w-4xl overflow-y-auto">
				<CardHeader>
					<div className="flex items-start justify-between gap-4">
						<div>
							<CardTitle>
								{threadSummary.subject ?? primaryMessage?.subject ?? "No subject"}
							</CardTitle>
							<CardDescription>
								Latest inbound {formatDate(primaryMessage?.created_at ?? threadSummary.updated_at)}{" "}
								from {threadSummary.participant_email}
							</CardDescription>
						</div>
						<div className="flex flex-wrap items-center gap-2">
							<Badge
								variant={messageStatusVariant[primaryMessage?.status ?? "received"] ?? "secondary"}
							>
								{primaryMessage?.status ?? "received"}
							</Badge>
							<Badge variant={intentVariant[analysis?.intent ?? "other"] ?? "secondary"}>
								{analysis?.intent ?? "unknown"}
							</Badge>
							<Badge variant={actionVariant[actionLabel] ?? "secondary"}>{actionLabel}</Badge>
						</div>
					</div>
				</CardHeader>

				<CardContent className="space-y-6">
					<div className="grid gap-6 md:grid-cols-2">
						<Section title="Customer">
							<div className="space-y-1 text-sm">
								<p className="font-medium">{threadSummary.participant_name ?? "Unknown sender"}</p>
								<p className="text-muted-foreground">{threadSummary.participant_email}</p>
								<p className="text-muted-foreground">Store: {threadSummary.store?.name ?? "-"}</p>
							</div>
						</Section>

						<Section title="Action Taken">
							<div className="space-y-2 text-sm">
								<p className="font-medium">{actionLabel}</p>
								{order?.shopify_order_number && (
									<p className="text-muted-foreground">
										Shopify reference #{order.shopify_order_number}
									</p>
								)}
								{order?.total_amount != null && (
									<p className="text-muted-foreground">
										Total {formatCurrency(order.total_amount)}
									</p>
								)}
								{order?.error_message && <p className="text-destructive">{order.error_message}</p>}
								{primaryMessage?.last_error && (
									<p className="text-destructive">{primaryMessage.last_error}</p>
								)}
								{primaryMessage?.status === "failed" && !order?.error_message && (
									<p className="text-destructive">
										Message processing failed before order creation.
									</p>
								)}
								{order?.shopify_order_id && threadSummary.store?.shopify_domain && (
									<a
										href={`https://${threadSummary.store.shopify_domain}/admin/orders/${order.shopify_order_id}`}
										target="_blank"
										rel="noopener noreferrer"
										className="inline-flex items-center gap-2 text-primary hover:underline"
									>
										Open in Shopify
										<ExternalLink className="h-4 w-4" />
									</a>
								)}
							</div>
						</Section>
					</div>

					<Section title="Conversation History">
						{threadLoading ? (
							<p className="text-sm text-muted-foreground">Loading conversation...</p>
						) : threadError ? (
							<p className="text-sm text-muted-foreground">
								Conversation history is temporarily unavailable.
							</p>
						) : !thread || thread.thread_messages.length === 0 ? (
							<p className="text-sm text-muted-foreground">No thread history available.</p>
						) : (
							<div className="space-y-3">
								{thread.thread_messages.map((threadMessage) => (
									<div key={threadMessage.id} className="rounded-md border p-4 text-sm">
										<div className="mb-2 flex flex-wrap items-center justify-between gap-2">
											<div>
												<p className="font-medium">
													{threadMessage.direction === "inbound" ? "Customer" : "You"}
												</p>
												<p className="text-xs text-muted-foreground">
													{threadMessage.sender_email} to {threadMessage.recipient_email}
												</p>
											</div>
											<div className="text-right text-xs text-muted-foreground">
												<p>{threadMessage.subject ?? thread.subject ?? "No subject"}</p>
												<p>{formatDate(threadMessage.sent_at ?? threadMessage.created_at)}</p>
											</div>
										</div>
										<div className="whitespace-pre-wrap rounded-md bg-muted/30 p-3">
											{threadMessage.body_text}
										</div>
									</div>
								))}
							</div>
						)}
					</Section>

					<Section title="AI Analysis">
						{analysis ? (
							<div className="space-y-4 text-sm">
								<div className="grid gap-4 md:grid-cols-3">
									<Info label="Intent" value={analysis.intent} />
									<Info label="Confidence" value={analysis.confidence} />
									<Info label="Urgency" value={analysis.urgency ?? "-"} />
								</div>
								<Info label="Summary" value={analysis.summary} multiline />
								<Info label="Customer Name" value={analysis.customer_name ?? "-"} />
								<Info label="Shipping Notes" value={analysis.shipping_notes ?? "-"} multiline />
								<div>
									<h3 className="mb-2 text-sm font-semibold">Extracted Items</h3>
									{analysis.items.length === 0 ? (
										<p className="text-muted-foreground">No items extracted.</p>
									) : (
										<div className="space-y-2">
											{analysis.items.map((item, index) => (
												<div
													key={`${item.product_query}-${index}`}
													className="rounded-md border p-3"
												>
													<div className="flex items-center justify-between gap-3">
														<p className="font-medium">{item.product_query}</p>
														<p className="text-muted-foreground">Qty {item.quantity}</p>
													</div>
													{item.notes && <p className="mt-1 text-muted-foreground">{item.notes}</p>}
												</div>
											))}
										</div>
									)}
								</div>
							</div>
						) : (
							<p className="text-sm text-muted-foreground">
								No AI analysis saved for this thread yet.
							</p>
						)}
					</Section>

					{order && (
						<Section title="Order Record">
							<div className="space-y-4 text-sm">
								<div className="grid gap-4 md:grid-cols-3">
									<Info label="Status" value={order.status} />
									<Info label="Confidence" value={order.confidence} />
									<Info
										label="Total"
										value={order.total_amount != null ? formatCurrency(order.total_amount) : "-"}
									/>
								</div>
								<Info label="Notes" value={order.notes ?? "-"} multiline />
								<div>
									<h3 className="mb-2 text-sm font-semibold">Line Items</h3>
									{order.items.length === 0 ? (
										<p className="text-muted-foreground">No line items saved.</p>
									) : (
										<div className="space-y-2">
											{order.items.map((item, index) => (
												<div
													key={`${item.product_name}-${index}`}
													className="flex items-center justify-between rounded-md border p-3"
												>
													<div>
														<p className="font-medium">{item.product_name}</p>
														{item.variant_title && (
															<p className="text-muted-foreground">{item.variant_title}</p>
														)}
													</div>
													<div className="text-right text-muted-foreground">
														<p>Qty {item.quantity}</p>
														<p>{item.unit_price != null ? formatCurrency(item.unit_price) : "-"}</p>
													</div>
												</div>
											))}
										</div>
									)}
								</div>
							</div>
						</Section>
					)}

					<details className="rounded-md border p-4">
						<summary className="flex cursor-pointer list-none items-center justify-between gap-2 font-medium">
							<span>Latest Inbound HTML</span>
							<ChevronDown className="h-4 w-4 text-muted-foreground" />
						</summary>
						<div className="mt-4 rounded-md bg-muted/30 p-4 text-xs whitespace-pre-wrap break-words">
							{primaryMessage?.body_html ?? "No HTML body saved."}
						</div>
					</details>

					<details className="rounded-md border p-4">
						<summary className="flex cursor-pointer list-none items-center justify-between gap-2 font-medium">
							<span>Latest Inbound Raw Payload</span>
							<ChevronDown className="h-4 w-4 text-muted-foreground" />
						</summary>
						<div className="mt-4 rounded-md bg-muted/30 p-4 text-xs whitespace-pre-wrap break-words">
							{primaryMessage?.raw_payload
								? JSON.stringify(primaryMessage.raw_payload, null, 2)
								: "No raw payload saved."}
						</div>
					</details>

					<div className="flex justify-end">
						<div className="flex gap-2">
							{retryableMessage && (
								<Button
									variant="outline"
									disabled={retryingMessageId === retryableMessage.id}
									onClick={() => void onRetry(retryableMessage.id)}
								>
									{retryingMessageId === retryableMessage.id ? "Retrying..." : "Retry Processing"}
								</Button>
							)}
							<Button variant="outline" onClick={onClose}>
								Close
							</Button>
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
	return (
		<div>
			<h2 className="mb-2 text-sm font-semibold">{title}</h2>
			<div className="rounded-md border p-4">{children}</div>
		</div>
	);
}

function Info({
	label,
	value,
	multiline = false,
}: {
	label: string;
	value: string;
	multiline?: boolean;
}) {
	return (
		<div>
			<p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
				{label}
			</p>
			<p className={cn("text-sm", multiline && "whitespace-pre-wrap")}>{value}</p>
		</div>
	);
}

function getPrimaryMessage(thread: ThreadWithRelations): ThreadMessageSummary | null {
	// Prefer a message that already has an associated order record so the
	// UI shows the correct "Action Taken" (eg. "Created draft order"). If no
	// message with an order exists, fall back to the latest inbound message.
	if (thread.messages) {
		const withOrder = thread.messages.find((m) => (m.orders?.length ?? 0) > 0);
		if (withOrder) return withOrder;
	}

	return thread.latestInboundMessage;
}

function getRetryableMessage(thread: ThreadWithRelations): ThreadMessageSummary | null {
	return thread.messages?.find((message) => message.status === "failed") ?? null;
}

function getAnalysis(message: ThreadMessageSummary | null): GeminiAnalysis | null {
	return (message?.gemini_analysis as GeminiAnalysis | null) ?? null;
}

function getPrimaryOrder(message: ThreadMessageSummary | null): ThreadOrderSummary | null {
	return message?.orders?.[0] ?? null;
}

function getActionLabel(
	message: ThreadMessageSummary | null,
	order: ThreadOrderSummary | null,
): string {
	if (order?.status === "placed") {
		return "Placed Shopify order";
	}

	if (order?.status === "draft") {
		return "Created draft order";
	}

	if (order?.status === "failed") {
		return "Order creation failed";
	}

	if (message?.status === "failed") {
		return "Processing failed";
	}

	if (message?.status === "processing" || message?.status === "received") {
		return "Awaiting processing";
	}

	return "No order created";
}

async function getErrorMessage(res: Response, fallback: string) {
	const contentType = res.headers.get("Content-Type") ?? "";

	if (contentType.includes("application/json")) {
		const body = await res.json();
		return body.error?.message ?? fallback;
	}

	const text = await res.text();
	return text || fallback;
}
